import { useState, type ReactNode } from "react";
import { Menu, X, LogOut, PanelLeftClose, PanelLeftOpen, ShoppingCart, ArrowLeft, type LucideIcon } from "lucide-react";

import { Button } from "./Button";
import { ThemeToggle } from "./Theme";
import { MyDailyDirectLogo } from "./MyDailyDirectLogo";

export interface NavItem {
  label: string;
  href: string;
  icon?: LucideIcon;
  badge?: number | string;
  onClick?: () => void;
}

export interface AppShellProps {
  appName: string;
  /** Short subtitle, e.g. the persona ("Retailer"). */
  subtitle?: string;
  nav: NavItem[];
  /** Optional bottom navigation items for mobile screens. If omitted, nav is converted. */
  bottomNav?: NavItem[];
  /** Whether to show the bottom navigation bar on desktop web as well as mobile. */
  showBottomNavOnDesktop?: boolean;
  /** Render prop for each nav link so the app supplies its router's Link + active state. */
  renderNavLink: (item: NavItem, className: string, isCollapsed?: boolean) => ReactNode;
  /** Render prop for mobile bottom nav item. */
  renderBottomNavLink?: (item: NavItem, className: string) => ReactNode;
  user?: {
    name: string;
    tenant?: string;
    phone?: string;
    orgId?: string;
    role?: string;
    accountType?: string;
  };
  onLogout?: () => void;
  cartBadgeCount?: number;
  onCartClick?: () => void;
  /** Whether the user can navigate back to a previous screen */
  canGoBack?: boolean;
  /** Callback triggered when the back button is clicked */
  onBack?: () => void;
  /** Optional custom max-width / container classes for the main content area */
  contentClassName?: string;
  children: ReactNode;
}

function formatDisplayRole(role?: string, accountType?: string, orgId?: string): string {
  const raw = (role || accountType || "").trim().toUpperCase();
  if (raw.includes("ADMIN") || raw === "ADMIN") return "Administrator";
  if (raw === "RM" || raw.includes("RELATIONSHIP") || raw.includes("MARKET")) return "Market Manager";
  if (raw === "TRADER" || raw.includes("DOCK")) return "Dock Admin";
  if (raw === "DRIVER") return "Delivery Driver";
  if (raw === "RETAILER" || raw.includes("BUYER")) return "Retailer";
  if (raw === "VENDOR") return "Vendor Partner";
  if (orgId && orgId.toLowerCase().includes("admin")) return "Administrator";
  if (role) return role;
  if (accountType) return accountType;
  return "Operations";
}

export function AppShell({
  appName: _appName,
  subtitle,
  nav,
  bottomNav,
  showBottomNavOnDesktop = false,
  renderNavLink,
  renderBottomNavLink,
  user,
  onLogout,
  cartBadgeCount = 0,
  onCartClick,
  canGoBack = false,
  onBack,
  contentClassName,
  children,
}: AppShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("mdd_sidebar_collapsed") === "true";
    } catch {
      return false;
    }
  });

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("mdd_sidebar_collapsed", String(next));
      } catch {}
      return next;
    });
  };

  // Professional nav link classes — active state uses sidebar accent tokens
  const navLinkClass = collapsed
    ? "flex h-9 w-9 items-center justify-center rounded text-sidebar-foreground/75 transition-all hover:bg-sidebar-accent hover:text-sidebar-foreground aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-sidebar-foreground aria-[current=page]:font-bold mx-auto"
    : "flex items-center gap-3 rounded px-3 py-2 text-sm font-medium text-sidebar-foreground/75 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground aria-[current=page]:text-sidebar-foreground aria-[current=page]:font-bold aria-[current=page]:bg-sidebar-accent";

  const navContent = (
    <nav className="flex flex-1 flex-col gap-0.5 px-3 py-3" aria-label="Primary">
      {nav.map((item) => renderNavLink(item, navLinkClass, collapsed))}
    </nav>
  );

  const brand = (
    <div className={`flex h-14 items-center border-b border-sidebar-border ${collapsed ? "justify-center px-0" : "px-4"}`}>
      <MyDailyDirectLogo size="md" showText={!collapsed} subtitle={subtitle} />
    </div>
  );

  const activeBottomNav = bottomNav || nav;

  return (
    <div className="flex h-full w-full overflow-hidden bg-background">
      {/* Desktop sidebar — locked full height, never scrolls with the page */}
      <aside
        className={`hidden h-full shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-all duration-200 ease-in-out lg:flex ${
          collapsed ? "w-[3.75rem]" : "w-56"
        }`}
      >
        <div className="shrink-0">{brand}</div>
        <div className="flex-1 overflow-y-auto thin-scrollbar">
          {navContent}
        </div>

        {/* User info at bottom — pinned at bottom of sidebar */}
        {user && (
          <div className="mt-auto shrink-0 border-t border-sidebar-border p-3">
            <div className={`flex items-center gap-2.5 ${collapsed ? "justify-center" : ""}`}>
              <div
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-sidebar-accent text-xs font-semibold text-sidebar-foreground border border-sidebar-border"
                title={collapsed ? user.name : undefined}
              >
                {user.name.charAt(0).toUpperCase()}
              </div>
              {!collapsed && (
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-sidebar-foreground">{user.name}</p>
                  <p className="truncate text-[10px] text-sidebar-foreground/60 font-mono">
                    {user.phone ? user.phone : user.tenant}
                  </p>
                </div>
              )}
              {onLogout && !collapsed && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onLogout}
                  aria-label="Sign out"
                  className="h-6 w-6 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent shrink-0"
                >
                  <LogOut className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        )}
      </aside>

      {/* Mobile slide-over menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-xs transition-opacity"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="absolute left-0 top-0 flex h-full w-72 max-w-[85vw] animate-slide-in-right flex-col border-r border-sidebar-border bg-sidebar shadow-2xl">
            <div className="flex h-14 items-center justify-between border-b border-sidebar-border px-4">
              <MyDailyDirectLogo size="sm" showText subtitle={subtitle} />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                className="h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
              >
                <X className="h-5 w-5 text-sidebar-foreground" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto" onClick={() => setMobileOpen(false)}>
              {navContent}
            </div>
            {user && (
              <div className="border-t border-sidebar-border p-4 bg-sidebar">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-sidebar-foreground truncate">{user.name}</p>
                    <p className="text-[10px] text-sidebar-foreground/70 font-mono truncate">{user.phone || user.tenant}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold bg-sidebar-accent text-sidebar-foreground border border-sidebar-border">
                      {formatDisplayRole(user.role, user.accountType, user.orgId)}
                    </span>
                  </div>
                  {onLogout && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onLogout}
                      className="text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent h-8 px-2.5 text-xs font-semibold shrink-0"
                    >
                      <LogOut className="h-3.5 w-3.5 mr-1" /> Sign out
                    </Button>
                  )}
                </div>
              </div>
            )}
          </aside>
        </div>
      )}

      {/* Main content wrapper — flex column, takes remaining width, 100% height */}
      <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden">
        {/* Top Header — pinned at top, never moves */}
        <header className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background/95 backdrop-blur-sm pl-2 pr-3 sm:pr-6 ds-chrome text-foreground">
          {/* Back button (shown on any screen when canGoBack is true) */}
          {canGoBack && onBack && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 text-foreground hover:bg-foreground/10 hover:text-foreground"
              onClick={onBack}
              aria-label="Go back"
              title="Go to previous screen"
            >
              <ArrowLeft className="h-5 w-5 text-foreground" />
            </Button>
          )}

          {/* Mobile hamburger menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 lg:hidden shrink-0 text-foreground hover:bg-foreground/10 hover:text-foreground"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5 text-foreground" />
          </Button>

          {/* Mobile brand logo */}
          <div className="lg:hidden flex items-center min-w-0">
            <MyDailyDirectLogo size="sm" showText subtitle={subtitle} />
          </div>

          {/* Desktop/tablet sidebar compress & expand toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleCollapse}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden lg:flex h-8 w-8 text-foreground/80 hover:text-foreground hover:bg-foreground/10"
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4 text-foreground" /> : <PanelLeftClose className="h-4 w-4 text-foreground" />}
          </Button>

          <div className="flex-1" />

          <div className="flex items-center gap-1.5 shrink-0">
            {/* Cart icon */}
            {onCartClick && (
              <button
                type="button"
                onClick={onCartClick}
                className="relative flex h-8 w-8 items-center justify-center rounded-lg text-foreground/80 hover:text-foreground hover:bg-foreground/10 transition-colors"
                title="View Cart"
                aria-label="View Cart"
              >
                <ShoppingCart className="h-4 w-4 text-foreground" />
                {cartBadgeCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-extrabold text-primary-foreground shadow-xs">
                    {cartBadgeCount}
                  </span>
                )}
              </button>
            )}

            {user && (
              <div className="hidden sm:flex items-center gap-2 pl-2.5 pr-1.5 border-l border-border/60">
                <div className="flex flex-col text-right leading-tight">
                  <span className="font-bold text-foreground text-xs truncate max-w-[140px]">{user.name}</span>
                  <span className="text-[10px] text-muted-foreground font-mono">
                    {user.phone ? user.phone : user.tenant}
                  </span>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary/10 text-primary border border-primary/25 tracking-wide">
                  {formatDisplayRole(user.role, user.accountType, user.orgId)}
                </span>
              </div>
            )}

            <ThemeToggle />

            {onLogout && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogout}
                className="hidden lg:inline-flex text-foreground/80 hover:text-foreground hover:bg-foreground/10 text-xs h-8 px-3"
              >
                Sign out
              </Button>
            )}
          </div>
        </header>

        {/* Scrollable View Area — ONLY THIS SCROLLS! */}
        <main
          id="app-main-content"
          className={`flex-1 overflow-y-auto overscroll-contain p-2.5 sm:p-5 lg:p-6 ${
            showBottomNavOnDesktop ? "pb-24" : "pb-24 lg:pb-8"
          }`}
        >
          <div className={`mx-auto w-full animate-fade-in ${contentClassName || "max-w-[1920px]"}`}>{children}</div>
        </main>
      </div>

      {/* Bottom Navigation Bar — available on mobile, and optionally pinned on desktop web */}
      <nav
        aria-label="Navigation Dock"
        className={`fixed bottom-0 z-40 flex h-16 items-center justify-around border-t border-border bg-background/95 backdrop-blur-md px-1 pb-[env(safe-area-inset-bottom,0px)] ds-chrome-bottom shadow-lg transition-all duration-200 ${
          showBottomNavOnDesktop
            ? collapsed
              ? "left-0 right-0 lg:left-[3.75rem]"
              : "left-0 right-0 lg:left-56"
            : "inset-x-0 lg:hidden"
        }`}
      >
        {activeBottomNav.map((item) => {
          const itemClass =
            "flex flex-1 min-w-0 h-full flex-col items-center justify-center py-1 px-0.5 text-[10px] font-medium text-muted-foreground transition-colors hover:text-foreground aria-[current=page]:text-primary aria-[current=page]:font-bold relative gap-0.5 select-none";

          if (renderBottomNavLink) {
            return renderBottomNavLink(item, itemClass);
          }
          return renderNavLink(item, itemClass);
        })}
      </nav>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
      <div className="min-w-0 sm:min-w-[280px] sm:basis-72 flex-1">
        <h1 className="text-lg font-semibold tracking-tight text-foreground sm:text-xl">{title}</h1>
        {description && (
          <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {actions && <div className="mt-1 sm:mt-0 flex flex-wrap items-center gap-2 max-w-full">{actions}</div>}
    </div>
  );
}

/**
 * Robustly resets all viewport scroll positions (window, html body, and AppShell <main>).
 */
export function scrollToTop() {
  if (typeof window !== "undefined") {
    window.scrollTo(0, 0);
  }
  if (typeof document !== "undefined") {
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
    const mainEl = document.getElementById("app-main-content") || document.querySelector("main");
    if (mainEl) {
      mainEl.scrollTop = 0;
    }
  }
}
